import { isValidElement, useCallback, useEffect, useRef, type ReactNode } from "react";

/**
 * Horisontal blaing mellom sider, som i feltappen.
 *
 * Bygd paa nettleserens egen rulling med `scroll-snap`, ikke paa pekerlyttere.
 * Paa en Mac blar man med to fingre paa styreflata, og det ER horisontal
 * rulling — da skal vi ikke staa i veien for den, men bruke den.
 *
 * To ting avgjoer om den foeles rask, og begge er maalt mot irritasjon og ikke
 * mot teori:
 *
 * 1. Siden leses HVERT BILDE mens du drar, ikke naar rullingen har lagt seg.
 *    Fanen og prikken foelger fingeren i stedet for aa ta igjen etterpaa. Det
 *    var den stoerste kilden til at det foeltes treigt — selve rullingen var
 *    rask nok.
 *
 * 2. `scroll-snap-stop: always` i stilarket gjoer at ett kast lander én side.
 *    Uten den reiser et raskt kast forbi flere sider og glir tilbake, og den
 *    reisen er tid man staar og venter paa naar man har daarlig tid.
 */
export function Pager({
  index, onIndexChange, children,
}: {
  readonly index: number;
  onIndexChange(i: number): void;
  readonly children: readonly ReactNode[];
}): JSX.Element {
  const spor = useRef<HTMLDivElement | null>(null);
  /** Maalet mens VI ruller. Null naar brukeren eier bevegelsen. */
  const vaart = useRef<number | null>(null);
  const bilde = useRef(0);
  /**
   * Hvilken side rulleposisjonen allerede staar paa.
   *
   * Dette er det som skiller «brukeren dro hit» fra «noen trykket paa en
   * fane», og uten det slaass de to: naar indeksen byttet midtveis i et kast,
   * startet effekten under en myk rulling mot den nye siden MENS fingeren
   * fortsatt dro. Det ga et hakk omtrent paa halvveien — bevegelsen stoppet og
   * fortsatte.
   */
  const siste = useRef(index);
  /** Hvor mange sider det var sist. Se under. */
  const antall = useRef(children.length);

  /*
   * Faner og prikker ruller dit. En gest gjoer det ikke — da staar vi stille.
   *
   * OG: BLIR LISTA LENGER, ER DET EN OMNUMMERERING — IKKE ET SIDEBYTTE.
   *
   * Sider kan komme til mens man staar paa en av dem. Siden man ser kan da
   * faa et nytt NUMMER uten at man har gaatt noe sted: Brief gaar fra plass 1
   * til plass 2 fordi Synk kom foran. Rullingen dit maa skje i ett hopp —
   * animeres den, ser det ut som flata byttet side, og det gjorde den ikke.
   */
  useEffect(() => {
    const el = spor.current;
    if (el === null) return;
    const nyListe = children.length !== antall.current;
    antall.current = children.length;
    if (index === siste.current && !nyListe) return;
    siste.current = index;
    const maal = index * el.clientWidth;
    if (Math.abs(el.scrollLeft - maal) < 2) return;
    vaart.current = maal;
    el.scrollTo({ left: maal, behavior: nyListe ? "auto" : "smooth" });
  }, [index, children.length]);

  const onScroll = useCallback((): void => {
    if (bilde.current !== 0) return;
    // Ett avlesning per bilde. Rullehendelser kommer tettere enn skjermen
    // oppdaterer, og en avlesning per hendelse er bortkastet arbeid.
    bilde.current = requestAnimationFrame(() => {
      bilde.current = 0;
      const el = spor.current;
      if (el === null || el.clientWidth === 0) return;

      const m = vaart.current;
      if (m !== null) {
        // Vaar egen rulling. Mellomverdiene underveis er ikke et sidebytte.
        if (Math.abs(el.scrollLeft - m) < 2) vaart.current = null;
        return;
      }

      const i = Math.round(el.scrollLeft / el.clientWidth);
      if (i !== siste.current) {
        siste.current = i;
        onIndexChange(i);
      }
    });
  }, [onIndexChange]);

  /*
   * Retter seg opp naar vinduet endrer bredde.
   *
   * Rulleposisjonen er lagret i piksler. Endrer bredden seg — et vindu som
   * dras, en rullefelt som dukker opp — stemmer ikke lenger `scrollLeft` med
   * sidetallet, og man havner MELLOM to sider: den ene klippes paa venstre
   * kant og den neste tyter inn fra hoeyre. Ingen animasjon her; det er en
   * korreksjon, ikke en bevegelse.
   */
  useEffect(() => {
    const el = spor.current;
    if (el === null) return;
    const ro = new ResizeObserver(() => {
      const maal = siste.current * el.clientWidth;
      if (Math.abs(el.scrollLeft - maal) < 2) return;
      vaart.current = maal;
      el.scrollTo({ left: maal, behavior: "auto" });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => () => cancelAnimationFrame(bilde.current), []);

  return (
    <div className="pager" ref={spor} onScroll={onScroll}>
      {/*
        * SLISSEN NOEKLES PAA BARNETS EGEN NOEKKEL, ikke paa plass i rekka.
        *
        * Sider kan komme og gaa mens appen staar. Med posisjonsnoekler
        * sammenlikner React slisse 1 med slisse 1, og naar innholdet der
        * byttet type, ble flata montert paa nytt: tilstanden i den forsvant —
        * rulleposisjon, halvskrevne felt, en innlest tegning. Barnets egen
        * noekkel foelger flata, ikke plassen.
        */}
      {children.map((barn, i) => (
        <section
          className="pager__side"
          key={isValidElement(barn) && barn.key !== null ? barn.key : i}
        >
          {barn}
        </section>
      ))}
    </div>
  );
}
